import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  Activity,
  AlertTriangle,
  Calendar,
  Shield,
  TrendingUp,
} from "lucide-react";
import React from "react";
import { useFacilityPerformance } from "../../../../hooks";

interface KpiSectionProps {
  facilityId: number;
}

export const KpiSection: React.FC<KpiSectionProps> = ({ facilityId }) => {
  const { data: performanceData } = useFacilityPerformance(facilityId);

  const performanceOverview = performanceData?.result?.overview;

  const formatRemainingLife = (value: number | undefined): string => {
    return value && value > 0 ? `${value} yrs` : "N/A";
  };

  const formatPercentage = (value: number | undefined): string => {
    return value !== undefined ? `${value}%` : "N/A";
  };

  const formatOutOfService = (
    count: number | undefined,
    percentage: number | undefined
  ): { count: string; displayValue: string } => {
    const safeCount = count ?? 0;
    const safePercentage = percentage ?? 0;
    return {
      count: safeCount.toString(),
      displayValue: `${safeCount} (${safePercentage}%)`,
    };
  };

  // Updated to use new API structure
  const outOfServiceData = formatOutOfService(
    performanceOverview?.outOfServiceAssets?.value,
    performanceOverview?.outOfServiceAssets?.percentage
  );

  const mainKPIs = [
    {
      title: "Facility Condition Index",
      count:
        performanceOverview?.facilityHealthIndex?.value?.toString() ?? "N/A",

      icon: <Activity className="h-8 w-8 text-blue-500" />,
      tooltip:
        "Overall health score combining asset condition, performance metrics, and maintenance status. Higher scores indicate better facility health.",
    },
    {
      title: "Asset Availability",
      count: formatPercentage(performanceOverview?.facilityAvailability?.value),

      icon: <Shield className="h-8 w-8 text-green-500" />,
      tooltip:
        "Percentage of assets currently operational and available for service. Excludes assets under maintenance or out of service.",
    },
    {
      title: "Out of Service",
      count: outOfServiceData.count,
      displayValue: outOfServiceData.displayValue,

      icon: <AlertTriangle className="h-8 w-8 text-orange-500" />,
      tooltip:
        "Number and percentage of assets currently out of service due to failures, maintenance, or scheduled repairs.",
    },
    {
      title: "Avg Remaining Life",
      count: formatRemainingLife(performanceOverview?.avgRemainingLife?.value),

      icon: <Calendar className="h-8 w-8 text-purple-500" />,
      tooltip:
        "Average remaining useful life across all facility assets based on age, condition, and maintenance history.",
    },
  ];

  const additionalKPIs = [
    {
      title: "Inspection Compliance",
      count: formatPercentage(performanceOverview?.inspectionCompliance?.value),

      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      tooltip:
        "Percentage of required inspections completed on time according to regulatory and maintenance schedules.",
    },
    {
      title: "Overall Risk Score",
      count: performanceOverview?.overallRiskScore?.value?.toString() ?? "N/A",

      icon: <Shield className="h-8 w-8 text-green-500" />,
      tooltip:
        "Composite risk assessment considering asset condition, failure probability, and operational impact. Lower scores indicate lower risk.",
    },
    {
      title: "Service Level Achievement",
      count: formatPercentage(
        performanceOverview?.serviceLevelAchievement?.value
      ),

      icon: <TrendingUp className="h-8 w-8 text-green-500" />,
      tooltip:
        "Percentage of service level agreements met, including response times, uptime targets, and performance standards.",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {mainKPIs.map((kpi, index) => (
          <QuickStatsCard
            key={index}
            title={kpi.title}
            count={kpi.count}
            displayValue={kpi.displayValue}
            icon={kpi.icon}
            tooltip={kpi.tooltip}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {additionalKPIs.map((kpi, index) => (
          <QuickStatsCard
            key={index}
            title={kpi.title}
            count={kpi.count}
            icon={kpi.icon}
            tooltip={kpi.tooltip}
          />
        ))}
      </div>
    </div>
  );
};
